import { Col, Row } from 'antd';
import { filter, get, isString, merge, pick } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import DotDotDot from 'react-dotdotdot';

import { IMAGES, STATUS_LIST } from '../../../enum';
import useDebouncedState from '../../../hooks/useDebouncedState';
import { momentTimezone } from '../../../utils/common';
import { formatNumberWithCurrency } from '../../../utils/numbers';
import { renderBooleanCheckmarks } from '../../../utils/tables';
import {
  getTripCompanyLocationFromAddressLabel,
  getTripCompanyLocationToAddressLabel,
  hasTripsWithDeductedCommuteMiles,
} from '../../../utils/trips';
import { canUpdateTripPurpose } from '../../../views/trips/trips-permissions';
import TripPurposeChip from '../../Chip/TripPurposeChip';
import ErrorBoundary from '../../ErrorBoundary';
import { Button, CustomTable } from '../../index';
import JourneyDistanceDisplay from '../../shared-ui/JourneyDistanceDisplay';
import StatusTag from '../../Tag/StatusTag';
import Text from '../../Text';
import FadedText from '../../Text/FadedText';
import LinkText from '../../Text/LinkText';
import Tooltip from '../../Tooltip';
import TripTypeSelect from '../../TripTypeSelect';
import ExpandedTripsSubRow from './ExpandedTripsSubRow';
import classNames from './style.module.scss';

/**
 * Table with Trips data
 */
const TripsTableByPeriod = props => {
  const {
    t,
    hideGPSColumns,
    authUserID,
    onDeleteTrip,
    displayAwaitingStatusHelp,
    tripsToRemove,
    onSearchChange,
    handleViewFullScreen,
    dataSource,
    companyCountry,
    isRecalculatingAmount,
    recalculatingTripId,
    onAmountRecalculate,
    onTripPurposeChange,
    isRejectingTrip,
    canRejectTrip,
    onTripReject,
    onViewMoreComments,
    onFetchDetails,
    onUpdateAndSubmit,
    ...rest
  } = props;
  const [fetchedTripDetails, setFetchedTripDetails] = useState({});
  const [fetchingRowKeys, setFetchingRowKeys] = useState([]);
  const [expandedRowsKeys, setExpandedRowsKeys] = useState([]);
  const [searchTerm, setSearchTerm] = useDebouncedState();

  const addFetchingRow = id => setFetchingRowKeys(array => [...array, id]);
  const removeFetchingRow = id => setFetchingRowKeys(array => filter(array, id));

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm);
    }
  }, [searchTerm, onSearchChange]);

  const COLUMNS = [
    ...(canRejectTrip
      ? [
          {
            title: '',
            width: 90,
            render: (actions, data) => {
              return (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isRejectingTrip}
                  onClick={e => {
                    e.stopPropagation();
                    onTripReject(data._id);
                  }}
                >
                  {t('reject')}
                </Button>
              );
            },
          },
        ]
      : []),
    {
      width: 100,
      key: 'journeyStartTs',
      title: t('date'),
      sorter: true,
      render: (actions, data) => {
        return (
          <p style={{ minWidth: 100, maxWidth: 'max-content' }}>
            {data.captureMode === 'Daily-Mileage' ? data.dateNoTimezone : data.date}
          </p>
        );
      },
    },
    {
      width: 140,
      key: 'time',
      title: t('time'),
      sorter: false,
      render: (actions, data) => {
        if (data.captureMode === 'Daily-Mileage') {
          return '-';
        }

        return (
          <p style={{ minWidth: 140, maxWidth: 'max-content' }}>
            {[
              momentTimezone(data.journeyStartTs).format('hh:mm a'),
              momentTimezone(data.journeyEndTs).format('hh:mm a'),
            ].join(' - ')}
          </p>
        );
      },
    },
    {
      width: 195,
      key: 'tripType',
      title: t('purpose'),
      sorter: true,

      render: (actions, data) => (
        <Row wrap={false} align="middle">
          {(!!data.notes || !!data.latestComment) && (
            <Col flex="40px">
              <img width="24px" height="24px" src={IMAGES.NOTES_PAPER_TEXT} alt="notes" />
            </Col>
          )}
          <Col flex={1}>
            {typeof onTripPurposeChange === 'function' && canUpdateTripPurpose(data) ? (
              <TripTypeSelect
                trip={data}
                disabled={rest?.loading}
                style={{ width: '100%' }}
                defaultValue="Business"
                value={data.tripType}
                onChange={purpose => onTripPurposeChange(data._id, purpose)}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            ) : (
              <>
                {typeof data.tripType === 'string' ? (
                  data.tripType === 'business-commute' ? (
                    <Row gutter={[0, 3]}>
                      <Col xs={24}>
                        <TripPurposeChip purpose={'commute'} />
                      </Col>
                      <Col xs={24}>
                        <TripPurposeChip purpose={'business'} />
                      </Col>
                    </Row>
                  ) : (
                    <TripPurposeChip purpose={data.tripType} />
                  )
                ) : (
                  '-'
                )}
              </>
            )}
          </Col>
        </Row>
      ),
    },
    {
      width: 100,
      title: t('Distance'),
      align: 'right',
      key: 'journeyDistance',
      dataIndex: 'journeyDistance',
      sorter: true,
      render: (distance, trip) => (
        <JourneyDistanceDisplay
          t={t}
          distance={distance}
          originalJourneyDistance={get(trip, 'originalJourneyDistance')}
          additionalJourneyDistance={get(trip, 'additionalJourneyDistance')}
        />
      ),
    },
    {
      width: 50,
      title: t('minutes'),
      align: 'right',
      key: 'duration',
      sorter: false,
      render: (actions, data) => {
        if (data.journeyStartTs && data.journeyEndTs) {
          return moment(data.journeyEndTs).diff(moment(data.journeyStartTs), 'minutes');
        }

        return '-';
      },
    },
    {
      width: 200,
      title: t('from'),
      key: 'from_loc',
      sorter: false,
      render: (actions, data) => {
        const stringLoc = isString(data.from_loc) ? data.from_loc : '';

        if (data.isFromAddressHome) {
          return (
            <Tooltip title={stringLoc}>
              <LinkText>{t('home')}</LinkText>
            </Tooltip>
          );
        }

        const companyLocationLabel = getTripCompanyLocationFromAddressLabel(data);
        if (companyLocationLabel) {
          return (
            <Tooltip title={stringLoc}>
              <LinkText>{companyLocationLabel}</LinkText>
            </Tooltip>
          );
        }

        return (
          <div title={stringLoc}>
            <DotDotDot clamp={2}>{stringLoc || '-'}</DotDotDot>
          </div>
        );
      },
    },
    {
      width: 200,
      title: t('to'),
      key: 'to_loc',
      sorter: false,
      render: (actions, data) => {
        const stringLoc = isString(data.to_loc) ? data.to_loc : '';

        if (data.isToAddressHome) {
          return (
            <Tooltip title={stringLoc}>
              <LinkText>{t('home')}</LinkText>
            </Tooltip>
          );
        }

        const companyLocationLabel = getTripCompanyLocationToAddressLabel(data);
        if (companyLocationLabel) {
          return (
            <Tooltip title={stringLoc}>
              <LinkText>{companyLocationLabel}</LinkText>
            </Tooltip>
          );
        }

        return (
          <div title={stringLoc}>
            <DotDotDot clamp={2}>{stringLoc || '-'}</DotDotDot>
          </div>
        );
      },
    },
    {
      width: 100,
      title: t('Amount'),
      key: 'pricing.totalAmount',
      dataIndex: ['pricing', 'totalAmount'],
      align: 'right',
      sorter: true,
      render: (actions, data) => {
        const amount = formatNumberWithCurrency(data.amount, data.currency);

        const TOOLTIP_VISIBLE = isRecalculatingAmount && recalculatingTripId === data._id;
        const CALCULATING_DIFFERENT_TRIP =
          isRecalculatingAmount && recalculatingTripId !== data._id;

        if (typeof onAmountRecalculate === 'function' && parseFloat(data.amount) === 0) {
          return (
            <Tooltip
              {...(TOOLTIP_VISIBLE
                ? { visible: true }
                : CALCULATING_DIFFERENT_TRIP
                ? { visible: false }
                : {})}
              className={classNames.amountTooltip}
              title={
                <Row onClick={e => e.stopPropagation()}>
                  <Col>
                    <Row>
                      <Text variant="p" size="sm">
                        {t('kliksHasNotYetCalculated')}
                      </Text>
                      <Text variant="p" size="sm">
                        {t('amountsAreCalculatedNightly')}
                      </Text>
                    </Row>

                    <Row style={{ margin: '15px 0' }}>
                      <Text variant="p" size="sm">
                        {t('toCalculateAmountNow')}:
                      </Text>
                    </Row>

                    <Row justify="center">
                      <Button
                        size="xs"
                        text="calculateTripAmount"
                        loading={isRecalculatingAmount}
                        onClick={e => {
                          e.stopPropagation();
                          onAmountRecalculate(data._id);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
              }
            >
              <Text textAlign="right" variant="p" size="sm" color="primary" renderAs="span">
                {amount}
              </Text>
            </Tooltip>
          );
        }

        return amount;
      },
    },
    {
      width: 130,
      key: 'crmMatch',
      title: t('crmMatch'),
      align: 'center',
      sorter: true,
      ...renderBooleanCheckmarks('isCRMTrip'),
    },
    {
      width: 50,
      title: t('GPS'),
      key: 'gpsVerified',
      align: 'center',
      sorter: true,
      ...renderBooleanCheckmarks('gps'),
    },
    {
      width: 160,
      title: t('status'),
      key: 'journeyStatus',
      render: (actions, data) => {
        if (data.status) {
          return (
            <>
              <StatusTag status={data.status} />

              {displayAwaitingStatusHelp &&
                typeof t === 'function' &&
                data.status === STATUS_LIST().Status.SUBMITTED && (
                  <FadedText size="sm">{t('tripApprovedWhenReceiptApproved')}</FadedText>
                )}
            </>
          );
        } else {
          return '-';
        }
      },
      sorter: true,
    },
  ];

  const toggleExpandedRow = row => {
    if (!expandedRowsKeys.includes(row.key)) {
      setExpandedRowsKeys([row.key]);
    } else {
      setExpandedRowsKeys([]);
    }
  };

  const hasOnlyDMLTrips = useMemo(() => {
    return dataSource.every(trip => trip.captureMode === 'Daily-Mileage');
  }, [dataSource]);

  const hiddenColumns = useMemo(() => {
    return hideGPSColumns && hasOnlyDMLTrips
      ? ['time', 'duration', 'from_loc', 'to_loc', 'gpsVerified', 'crmMatch']
      : [];
  }, [hideGPSColumns, hasOnlyDMLTrips]);

  return (
    <CustomTable
      rowKey={data => data._id}
      showSearchInput={false}
      onSearchTermChange={setSearchTerm}
      {...rest}
      hiddenColumns={hiddenColumns}
      dataSource={dataSource}
      className="trips-table"
      rowClassName={data => {
        if (Array.isArray(tripsToRemove) && tripsToRemove.includes(data._id)) {
          return classNames.blurredRow;
        }
      }}
      withExpandableRows
      columns={COLUMNS}
      scroll={{ x: 1400 }}
      onRow={row => ({
        className: 'trips-table-row',
        leftBorderColor: hasTripsWithDeductedCommuteMiles([row]) ? 'danger' : '',
        onClick: e => {
          e.preventDefault();
          e.stopPropagation();
          toggleExpandedRow(row);
        },
      })}
      expandedRowKeys={expandedRowsKeys}
      onExpand={(expanded, record) => {
        toggleExpandedRow(record);
        if (
          typeof onFetchDetails === 'function' &&
          expanded &&
          !Object.keys(fetchedTripDetails).includes(record._id) &&
          !fetchingRowKeys.includes(record._id)
        ) {
          addFetchingRow(record._id);
          onFetchDetails(record._id)
            .then(tripDetails => {
              setFetchedTripDetails(obj => {
                return merge(obj, {
                  [tripDetails._id]: merge(record, pick(tripDetails, ['_id', 'journeyGeoJson'])),
                });
              });
            })
            .finally(() => removeFetchingRow(record._id));
        }
      }}
      expandedRowRender={data => (
        <ErrorBoundary>
          <ExpandedTripsSubRow
            t={t}
            trip={data}
            authUserID={authUserID}
            onDeleteTrip={onDeleteTrip}
            companyCountry={companyCountry}
            onViewMoreComments={onViewMoreComments}
            onViewFullScreenClick={handleViewFullScreen}
            onUpdateAndSubmit={onUpdateAndSubmit}
          />
        </ErrorBoundary>
      )}
    />
  );
};

export default TripsTableByPeriod;
